import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Archive, RefreshCw } from 'lucide-react';
import { DataTable, EmptyState, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { Badge, Button } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { formatDate } from '@/lib/format-date';
import { useBackupList, useDeleteBackup, type Backup } from '@/api/opennebula';
import { BackupRestoreDialog } from './backups/BackupRestoreDialog';
import { RowMenu } from './components/CrudBits';
import { ListPageShell, TableSkeleton, ErrorState, linkStyle, Dash, mbToGB } from './components/ListPageShell';

function stateTone(state?: string): StatusTone {
  if (!state) return 'muted';
  if (/ready/i.test(state)) return 'success';
  if (/error|fail/i.test(state)) return 'danger';
  return 'warning';
}

/**
 * Service-wide backups. Backups are created from an instance (Backups tab); this
 * page lists them all and drives the cross-cutting actions — Restore (clone to a
 * new instance or in-place) and Delete. Mirrors the legacy top-level Backups list.
 */
export function Backups() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: backups = [], isLoading, isError, refetch } = useBackupList(serviceId);
  const [restore, setRestore] = useState<Backup | null>(null);
  const [del, setDel] = useState<Backup | null>(null);
  const base = `/services/compute/${serviceId}`;

  const remove = useDeleteBackup(serviceId!, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Backup deletion requested' });
      setDel(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<Backup, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span>,
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge tone={row.original.backup_increments == null ? 'info' : 'neutral'} appearance="subtle" size="sm">
          {row.original.backup_increments == null ? 'Full' : 'Incremental'}
        </Badge>
      ),
    },
    {
      id: 'instance',
      header: 'Instance',
      cell: ({ row }) =>
        row.original.attached?.instance_id ? (
          <Link to={`${base}/instances/${row.original.attached.instance_id}`} style={linkStyle}>
            {row.original.attached.name}
          </Link>
        ) : (
          <Dash />
        ),
    },
    { id: 'size', header: 'Size', cell: ({ row }) => <span>{mbToGB(row.original.size)} GB</span> },
    {
      id: 'state',
      header: 'State',
      cell: ({ row }) => <StatusPill tone={stateTone(row.original.state)}>{row.original.state || '—'}</StatusPill>,
    },
    {
      id: 'created',
      header: 'Created',
      cell: ({ row }) => <span>{row.original.created_at ? formatDate(String(row.original.created_at)) : '—'}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        // Restore/Delete only apply to a fully-written backup; in-progress rows
        // have no usable actions.
        const ready = /ready/i.test(row.original.state ?? '');
        if (!ready) return <Dash />;
        return (
          <RowMenu
            items={[
              { label: 'Restore', onSelect: () => setRestore(row.original) },
              { label: 'Delete', danger: true, onSelect: () => setDel(row.original) },
            ]}
          />
        );
      },
    },
  ];

  return (
    <ListPageShell
      icon={<Archive size={22} />}
      title="Backups"
      description="All instance backups across this service. Restore to a new or existing instance, or delete."
      actions={
        <Button size="sm" variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
          Refresh
        </Button>
      }
    >
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState description="Could not load backups for this service." onRetry={() => refetch()} />
      ) : (
        // DataTable owns the bordered frame; the empty placeholder renders
        // inside it via `emptyState`, so empty and populated share one frame
        // (no extra Card wrapper, no double border).
        <DataTable
          columns={columns}
          data={backups}
          getRowId={(b) => String(b.id)}
          emptyState={
            <EmptyState
              icon={<Archive size={28} />}
              title="No backups"
              description="Backups you take from an instance's Backups tab will appear here."
            />
          }
        />
      )}

      <BackupRestoreDialog backup={restore} onClose={() => setRestore(null)} />

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete backup?"
        description={`Permanently delete “${del?.name}”. This cannot be recovered.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </ListPageShell>
  );
}
